import { useState } from 'react';
import { FormattedMessage, defineMessages } from 'react-intl';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { TabContainer } from '@sorare/core/src/atoms/layout/TabContainer';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';

import { SpentFiatPaymentIntents } from '../SpentFiatPaymentIntents';
import { UserAccountEntries } from '../UserAccountEntries';

enum TransactionTabs {
  ETH = 'ETH',
  FIAT = 'FIAT',
  CREDIT_CARD = 'CREDIT_CARD',
}

const CurrencyType = {
  [TransactionTabs.FIAT]: Currency.FIAT,
  [TransactionTabs.ETH]: Currency.ETH,
};

const isTransactionTab = (str: string): str is TransactionTabs => {
  return Object.values(TransactionTabs).some(tab => tab === str);
};

const messages = defineMessages({
  [TransactionTabs.FIAT]: {
    id: 'TransactionTabs.fiat',
    defaultMessage: 'Cash Wallet',
  },
  [TransactionTabs.ETH]: {
    id: 'PaymentBox.eth',
    defaultMessage: 'ETH Wallet',
  },
  [TransactionTabs.CREDIT_CARD]: {
    id: 'TransactionTabs.creditCard',
    defaultMessage: 'Credit card',
  },
});

type Props = {
  short?: boolean;
};

export const TransactionsHistoryContent = ({ short }: Props) => {
  const {
    walletPreferences: { showFiatWallet, showEthWallet },
  } = useCurrentUserContext();

  const initTab = showEthWallet ? TransactionTabs.ETH : TransactionTabs.FIAT;
  const [tab, setTab] = useState<TransactionTabs>(initTab);

  return (
    <>
      <TabBar
        fullWidth
        value={tab}
        variant="flat"
        onValueChange={val => {
          if (!isTransactionTab(val)) {
            return;
          }
          setTab(val);
        }}
      >
        {showEthWallet && (
          <Tab value={TransactionTabs.ETH}>
            <FormattedMessage {...messages.ETH} />
          </Tab>
        )}
        {showFiatWallet && (
          <Tab value={TransactionTabs.FIAT}>
            <FormattedMessage {...messages.FIAT} />
          </Tab>
        )}

        <Tab value={TransactionTabs.CREDIT_CARD}>
          <FormattedMessage {...messages.CREDIT_CARD} />
        </Tab>
      </TabBar>
      <TabContainer>
        {(tab === TransactionTabs.ETH || tab === TransactionTabs.FIAT) && (
          <UserAccountEntries currency={CurrencyType[tab]} short={short} />
        )}
        {tab === TransactionTabs.CREDIT_CARD && (
          <SpentFiatPaymentIntents short={short} />
        )}
      </TabContainer>
    </>
  );
};
